import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PODIUM_URL } from "@/lib/assets";
import { toast } from "sonner";

export function MemberHome() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/member/summary").then(r => setS(r.data)); }, []);
  const tierLabel = user?.membership_tier === "inner_circle" ? "Inner Circle"
                  : user?.membership_tier === "chambers" ? "Auntie's Chambers"
                  : "Free Community";
  return (
    <div>
      <div className="brand-eyebrow">Welcome back</div>
      <h1 className="font-serif text-4xl md:text-5xl text-charcoal mt-2">Come on in, {user?.name?.split(" ")[0]}.</h1>
      <p className="brand-lede mt-4">You're seated in <span className="text-plum">{tierLabel}</span>.</p>

      <div className="mt-10 grid md:grid-cols-3 gap-6">
        <Metric label="Saved items" value={s?.saved_count ?? "…"} />
        <Metric label="Registered events" value={s?.registrations ?? "…"} />
        <Metric label="Ticketed purchases" value={s?.purchases ?? "…"} />
      </div>

      <section className="mt-12 grid md:grid-cols-2 gap-6">
        <Link to="/member/videos" className="editorial-card p-6 block" data-testid="member-quick-videos">
          <div className="brand-eyebrow">Exclusive Videos</div>
          <h3 className="font-serif text-2xl mt-2">Enter the library</h3>
          <p className="text-charcoal/60 text-sm mt-2">Firesides, private sessions, and replays reserved for members.</p>
        </Link>
        <Link to="/member/questions" className="editorial-card p-6 block" data-testid="member-quick-questions">
          <div className="brand-eyebrow">Ask Auntie</div>
          <h3 className="font-serif text-2xl mt-2">Submit a question</h3>
          <p className="text-charcoal/60 text-sm mt-2">Paid members get priority in Auntie's weekly review.</p>
        </Link>
      </section>

      <section className="mt-14 rounded-lg overflow-hidden relative">
        <img src={PODIUM_URL} alt="" className="w-full h-64 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-plum/90 to-plum/40 flex items-center px-10">
          <div className="text-cream max-w-md">
            <div className="script-auntie text-4xl text-gold">Auntie says</div>
            <div className="font-serif text-2xl mt-2 leading-tight">"Show up for yourself first. The rest will show up too."</div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="editorial-card p-6">
      <div className="brand-eyebrow">{label}</div>
      <div className="font-serif text-4xl text-plum mt-2">{value}</div>
    </div>
  );
}

// Exclusive videos view (uses main /videos and filters)
export function MemberVideos() {
  const [videos, setVideos] = useState([]);
  useEffect(() => { api.get("/videos").then(r => setVideos(r.data.filter(v => v.access_level !== "public"))); }, []);
  return (
    <div>
      <div className="brand-eyebrow">Member Library</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Exclusive Videos</h1>
      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.map(v => (
          <Link key={v.id} to={`/videos/${v.id}`} className="editorial-card block" data-testid={`mvideo-${v.id}`}>
            <img src={v.thumbnail} alt="" className="w-full aspect-video object-cover" />
            <div className="p-5">
              <div className="brand-eyebrow">{v.category}</div>
              <h3 className="font-serif text-lg mt-2">{v.title}</h3>
            </div>
          </Link>
        ))}
        {videos.length === 0 && <div className="text-charcoal/50">No member-only videos yet.</div>}
      </div>
    </div>
  );
}

export function MemberEvents() {
  const [regs, setRegs] = useState([]);
  useEffect(() => { api.get("/member/registrations").then(r => setRegs(r.data)); }, []);
  return (
    <div>
      <div className="brand-eyebrow">My Events</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Registered & Purchased</h1>
      <div className="mt-8 space-y-4">
        {regs.map(r => r.event && (
          <Link key={r.id} to={`/events/${r.event.id}`} className="editorial-card p-5 flex gap-5 items-center" data-testid={`mreg-${r.id}`}>
            <img src={r.event.cover_image} alt="" className="w-32 h-20 object-cover rounded" />
            <div className="flex-1">
              <div className="brand-eyebrow">{new Date(r.event.starts_at).toLocaleString()}</div>
              <h3 className="font-serif text-xl mt-1">{r.event.title}</h3>
            </div>
          </Link>
        ))}
        {regs.length === 0 && <div className="text-charcoal/50">No registered events yet. <Link to="/events" className="text-plum underline">Browse events</Link>.</div>}
      </div>
    </div>
  );
}

export function MemberSaved() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/member/saved").then(r => setItems(r.data)); }, []);
  return (
    <div>
      <div className="brand-eyebrow">Saved Content</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Your library</h1>
      <div className="mt-8 space-y-4">
        {items.map(s => s.item && (
          <Link key={s.id} to={s.kind === "video" ? `/videos/${s.item.id}` : `/blog/${s.item.slug || s.item.id}`} className="editorial-card p-5 flex gap-5 items-center" data-testid={`msave-${s.id}`}>
            <div className="w-24 h-16 bg-cream-alt rounded overflow-hidden">{s.item.cover_image || s.item.thumbnail ? <img src={s.item.cover_image || s.item.thumbnail} alt="" className="w-full h-full object-cover" /> : null}</div>
            <div className="flex-1">
              <div className="brand-eyebrow">{s.kind === "video" ? "Video" : "Post"}</div>
              <h3 className="font-serif text-lg mt-1">{s.item.title}</h3>
            </div>
          </Link>
        ))}
        {items.length === 0 && <div className="text-charcoal/50">Nothing saved yet — bookmark videos and posts from around the site.</div>}
      </div>
    </div>
  );
}

export function MemberResources() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/resources").then(r => setItems(r.data)); }, []);
  return (
    <div>
      <div className="brand-eyebrow">Downloads</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Guides & Resources</h1>
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        {items.map(r => (
          <div key={r.id} className="editorial-card p-6" data-testid={`mres-${r.id}`}>
            <div className="brand-eyebrow">{r.access_level}</div>
            <h3 className="font-serif text-xl mt-2">{r.title}</h3>
            <p className="text-sm text-charcoal/60 mt-2">{r.description}</p>
            {r.file_url ? (
              <a href={r.file_url} download className="btn-outline inline-flex mt-4 text-xs">Download</a>
            ) : (
              <div className="mt-4 text-xs text-charcoal/40">Coming soon</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function MemberQuestions() {
  const [form, setForm] = useState({ subject: "", body: "", is_priority: false });
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/member/questions").then(r => setMine(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/member/questions", form); setForm({ subject: "", body: "", is_priority: false }); load(); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <div className="brand-eyebrow">Ask Auntie</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Submit a Question</h1>
      <form onSubmit={submit} className="mt-8 editorial-card p-6 space-y-4 max-w-2xl">
        <input required value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} placeholder="Subject" data-testid="q-subject"
          className="w-full rounded-md border border-gold-light bg-cream px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gold" />
        <textarea required rows={5} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} placeholder="Your question…" data-testid="q-body"
          className="w-full rounded-md border border-gold-light bg-cream px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold" />
        <label className="flex items-center gap-2 text-sm text-charcoal/70">
          <input type="checkbox" checked={form.is_priority} onChange={(e) => setForm({...form, is_priority: e.target.checked})} data-testid="q-priority" />
          Priority (paid members)
        </label>
        <button disabled={busy} className="btn-primary" data-testid="q-submit">{busy ? "…" : "Submit"}</button>
      </form>
      <h2 className="mt-12 brand-h3">Your questions</h2>
      <div className="mt-4 space-y-3">
        {mine.map(q => (
          <div key={q.id} className="editorial-card p-4">
            <div className="brand-eyebrow">{q.status}</div>
            <div className="font-serif text-lg mt-1">{q.subject}</div>
            <div className="text-sm text-charcoal/70 mt-2">{q.body}</div>
            {q.answer && <div className="mt-3 pl-4 border-l-2 border-gold text-charcoal/90"><b className="text-plum">Auntie:</b> {q.answer}</div>}
          </div>
        ))}
        {mine.length === 0 && <div className="text-charcoal/50">No questions yet.</div>}
      </div>
    </div>
  );
}

export function MemberAccount() {
  const { user, refresh } = useAuth();
  const tier = user?.membership_tier;
  const [pw, setPw] = useState({ old_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const changePw = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password updated.");
      setPw({ old_password: "", new_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  const cancelSub = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of the current period.")) return;
    setCancelling(true);
    try {
      const { data } = await api.post("/member/cancel-subscription");
      toast.success(data.message || "Cancelled.");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel");
    } finally { setCancelling(false); }
  };

  return (
    <div>
      <div className="brand-eyebrow">Account & Billing</div>
      <h1 className="font-serif text-4xl text-charcoal mt-2">Your account</h1>
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="editorial-card p-6">
          <div className="brand-eyebrow">Profile</div>
          <div className="mt-3 space-y-2">
            <div><span className="text-charcoal/60">Name:</span> <b>{user?.name}</b></div>
            <div><span className="text-charcoal/60">Email:</span> <b>{user?.email}</b></div>
            <div><span className="text-charcoal/60">Role:</span> <b>{user?.role}</b></div>
          </div>
        </div>
        <div className="editorial-card p-6">
          <div className="brand-eyebrow">Membership</div>
          <div className="mt-3 space-y-2">
            <div><span className="text-charcoal/60">Tier:</span> <b>{tier || "community"}</b></div>
            <div><span className="text-charcoal/60">Billing:</span> <b>{user?.membership_billing || "—"}</b></div>
            <div><span className="text-charcoal/60">Status:</span> <b>{user?.membership_status || "—"}</b></div>
            <div><span className="text-charcoal/60">Renews / expires:</span> <b>{user?.membership_expires_at ? new Date(user.membership_expires_at).toLocaleDateString() : "—"}</b></div>
          </div>
          <div className="mt-6 flex gap-3">
            {(tier === "community" || !tier) && <Link to="/membership" className="btn-primary inline-flex" data-testid="account-upgrade">Upgrade</Link>}
            {user?.role === "paid_member" && user?.membership_status === "active" && (
              <button onClick={cancelSub} disabled={cancelling} className="btn-outline text-xs" data-testid="account-cancel">
                {cancelling ? "…" : "Cancel subscription"}
              </button>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={changePw} className="mt-8 editorial-card p-6 max-w-xl space-y-4">
        <div className="brand-eyebrow">Security</div>
        <h3 className="font-serif text-2xl">Change password</h3>
        <div>
          <label className="text-sm text-charcoal/70">Current password</label>
          <input type="password" required minLength={1} value={pw.old_password} onChange={(e) => setPw({...pw, old_password: e.target.value})} data-testid="account-pw-old"
            className="mt-2 w-full rounded-md border border-gold-light bg-cream px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gold" />
        </div>
        <div>
          <label className="text-sm text-charcoal/70">New password (min 8 chars)</label>
          <input type="password" required minLength={8} value={pw.new_password} onChange={(e) => setPw({...pw, new_password: e.target.value})} data-testid="account-pw-new"
            className="mt-2 w-full rounded-md border border-gold-light bg-cream px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gold" />
        </div>
        <button disabled={busy} className="btn-primary" data-testid="account-pw-submit">{busy ? "…" : "Update password"}</button>
      </form>
    </div>
  );
}
